import { useCallback, useEffect } from "react";

import { useBoolean } from "@/hooks/useBoolean";
import { PageProps } from "@/types/framework7-react";

type Options = {
  defaultReady?: boolean;
  isTabPage?: boolean;
};

export function usePageReady(pageName: string, options?: Options) {
  const { value: isPageReady, setTrue: readyOn, setFalse: readyOff } = useBoolean(Boolean(options?.defaultReady));

  useEffect(() => {
    console.debug("PageReady", pageName, "isPageReady", isPageReady);
  }, [pageName, isPageReady]);

  const getPageProps = useCallback(
    (externalProps?: Partial<PageProps>): PageProps => {
      return {
        ...externalProps,

        onPageAfterIn: (page) => {
          console.debug("PageReady", pageName, "onPageAfterIn", page);
          externalProps?.onPageAfterIn?.(page);
        },

        onPageAfterOut: (page) => {
          console.debug("PageReady", pageName, "onPageAfterOut", page);
          externalProps?.onPageAfterOut?.(page);
        },

        onPageBeforeIn: (page) => {
          console.debug("PageReady", pageName, "onPageBeforeIn", page);
          if (!options?.isTabPage) {
            readyOn();
          }
          externalProps?.onPageBeforeIn?.(page);
        },

        onPageBeforeOut: (page) => {
          console.debug("PageReady", pageName, "onPageBeforeOut", page);
          externalProps?.onPageBeforeOut?.(page);
        },

        onPageBeforeRemove: (page) => {
          console.debug("PageReady", pageName, "onPageBeforeRemove", page);
          if (!options?.isTabPage) {
            readyOff();
          }
          externalProps?.onPageBeforeRemove?.(page);
        },

        onPageInit: (page) => {
          console.debug("PageReady", pageName, "onPageInit", page);
          externalProps?.onPageInit?.(page);
        },

        onPageMounted: (page) => {
          console.debug("PageReady", pageName, "onPageMounted", page);
          externalProps?.onPageMounted?.(page);
        },

        onPageTabHide: (page) => {
          console.debug("PageReady", pageName, "onPageTabHide", page);
          if (options?.isTabPage) {
            readyOff();
          }
          externalProps?.onPageTabHide?.(page);
        },

        onPageTabShow: (page) => {
          console.debug("PageReady", pageName, "onPageTabShow", page);
          if (options?.isTabPage) {
            readyOn();
          }
          externalProps?.onPageTabShow?.(page);
        },
      };
    },
    [options?.isTabPage, pageName, readyOff, readyOn],
  );

  return { getPageProps, isPageReady };
}
